/**
 * Tabs Controller
 *
 * Handles tab switching functionality with smooth transitions
 * and ARIA accessibility support.
 */

import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["tab", "panel", "image"];
  static values = {
    active: { type: Number, default: 0 },
  };

  connect() {
    // Set initial state
    this.showPanel(this.activeValue);
  }

  /**
   * Handle tab selection
   * @param {Event} event - Click event from tab button
   */
  select(event) {
    event.preventDefault();
    const index = parseInt(event.currentTarget.dataset.index, 10);

    if (index === this.activeValue) {
      return; // Already active
    }

    this.activeValue = index;
    this.showPanel(index);
  }

  /**
   * Show the panel at the given index
   * @param {number} index - Index of the panel to show
   */
  showPanel(index) {
    // Update tabs
    this.tabTargets.forEach((tab, i) => {
      const isActive = i === index;
      const icon = tab.querySelector(".nr-tab-icon");
      const label = tab.querySelector(".nr-tab-label");

      // Update active class
      tab.classList.toggle("is-active", isActive);

      // Update aria-selected
      tab.setAttribute("aria-selected", isActive ? "true" : "false");

      // Update icon filter for inactive state (gray color rgba(166, 168, 169, 1))
      if (icon) {
        if (isActive) {
          icon.style.filter = "";
        } else {
          icon.style.filter = "brightness(0) saturate(100%) invert(73%) sepia(3%) saturate(90%) hue-rotate(169deg) brightness(93%) contrast(87%)";
        }
      }

      // Update label color
      if (label) {
        label.classList.toggle("text-[#3171a6]", isActive);
        label.classList.toggle("text-[#a9adb1]", !isActive);
      }
    });

    // Update panels
    this.panelTargets.forEach((panel, i) => {
      const isActive = i === index;
      panel.classList.toggle("hidden", !isActive);
    });

    // Update mobile images
    this.imageTargets.forEach((image, i) => {
      const isActive = i === index;
      image.classList.toggle("hidden", !isActive);
    });

    // Dispatch custom event
    this.dispatch("changed", { detail: { index } });
  }

  /**
   * Called when activeValue changes
   */
  activeValueChanged() {
    this.showPanel(this.activeValue);
  }
}
